import { msg } from "@lingui/core/macro";
import { createRoute, Outlet, useNavigate, useParams } from "@tanstack/solid-router";
import { appRoute } from "./route";
import { createMemo, Show } from "solid-js";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { NotFoundState } from "@/components/ui/not-found-state";
import { useSession } from "@/lib/auth";
import { TodoListView } from "@/features/todos/components/todo-list-view";
import {
  getSavedViewQuery,
  getTodosCountQuery,
  getTodosQuery,
  parseSavedViewFilters,
  queryKeys,
  savedViewFiltersToTodosOptions,
} from "@listto/shared";
import { Filter } from "@fleet/icons";
import { createAccountPowerSyncSingle } from "@/lib/powersync/create-account-query";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import type { PowerSyncDrizzleDB } from "@listto/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

/**
 * Route of a saved smart view: loads the `saved_view` row, translates
 * its persisted filters to `UseTodosOptions` and renders the SAME
 * `TodoListView` as the fixed views (the saved view is literally a
 * named `getTodosQuery`).
 */
function SavedViewPage() {
  const { t } = useI18n();
  const params = useParams({ from: "/_app/views/$viewId" });
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;
  const navigate = useNavigate();

  const viewId = () => params().viewId;

  const view = createAccountPowerSyncSingle({
    queryKey: () => queryKeys.savedViews.detail(viewId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getSavedViewQuery(dbInstance, viewId()) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  // Resolves the view: present / loading (sync) / not-found / offline.
  const resolution = createEntityResolution({
    db: () => db(),
    hasEntity: () => Boolean(view.data),
    isPending: () => view.isLoading,
  });

  const options = createMemo(() =>
    savedViewFiltersToTodosOptions(parseSavedViewFilters(view.data?.filters)),
  );

  return (
    <Show
      when={resolution() === "ready" || resolution() === "loading"}
      fallback={
        <NotFoundState
          variant={resolution() === "offline" ? "offline" : "not-found"}
          offerAccountSwitch={resolution() === "not-found"}
          title={resolution() === "offline" ? undefined : t(msg`View not found`)}
          description={
            resolution() === "offline" ? undefined : t(msg`This saved view may have been deleted.`)
          }
          primaryAction={{
            label: t(msg`Go to my tasks`),
            onClick: () => {
              void navigate({ to: "/" });
            },
          }}
        />
      }
    >
      <TodoListView
        title={view.data?.name ?? t(msg`Loading...`)}
        icon={Filter}
        userId={userId()}
        queryFn={(dbInstance: PowerSyncDrizzleDB, uid: string, queryOptions) =>
          getTodosQuery(dbInstance, uid, { ...queryOptions, ...options() })
        }
        countQueryFn={(dbInstance, uid) => getTodosCountQuery(dbInstance, uid, options())}
        emptyMessage={t(msg`No tasks match this view.`)}
        listId={`view-${viewId()}`}
      />
      <Outlet />
    </Show>
  );
}

export const savedViewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/views/$viewId",
  component: SavedViewPage,
});
